/**
 * Predictive Search — Real-time product suggestions via Shopify Predictive Search API
 * Supports: Desktop header search + Mobile header search
 * Version: 1.0.0
 */

(function () {
  'use strict';

  var PredictiveSearch = {

    /* ── Configuration ─────────────────────────────── */
    config: {
      endpoint:      '/search/suggest.json',
      types:         'product',          // product, article, page, collection
      limit:         6,                  // max results shown
      debounceMs:    220,                // ms to wait after keystroke
      minChars:      2,                  // min chars to trigger search
      zeroPadMax:    4,                  // leading zeros a SKU query is retried with
      zeroPadMinChars: 3,                // shortest query worth retrying
      highlightTag:  'mark',             // tag used to wrap matched text
      viewportGap:   12                  // px kept between the phone list and the keyboard
    },

    /* ── State ─────────────────────────────────────── */
    _debounceTimer: null,
    _activeIndex:   -1,
    _lastQuery:     '',
    _cache:         {},
    _requestSeq:    0,
    _mobilePanels:  [],

    /* ── Init ───────────────────────────────────────── */
    init: function () {
      var self = this;

      // Desktop search
      var desktopInput = document.getElementById('DesktopStickySearchInput') || document.getElementById('DesktopSearchInput');
      var desktopResults = document.getElementById('DesktopStickySearchResults') || document.getElementById('DesktopSearchResults');
      if (desktopInput && desktopResults) {
        self._bindInput(desktopInput, desktopResults, 'desktop');
      }

      // Mobile search
      var mobileInput = document.getElementById('MobileHeaderSearchInput');
      var mobileResults = document.getElementById('MobileHeaderSearchResults');
      if (mobileInput && mobileResults) {
        self._bindInput(mobileInput, mobileResults, 'mobile');
        self._mobilePanels.push(mobileResults);
        var clearButton = mobileInput.form ? mobileInput.form.querySelector('[data-search-clear]') : null;
        if (clearButton) { self._bindClear(mobileInput, mobileResults, clearButton); }
      }

      /*
        THE PAGE HOLDS STILL UNDER THE OPEN PHONE LIST. html.bc-search-open
        (bc-responsive.css, section 9) takes the scroll off the page without
        moving it - the search row lives in the sticky header, so the viewer's
        position:fixed body lock would carry it out of view. Safari still
        scrolls the page by touch through overflow:hidden on the root, so a
        touch move that does not start inside a list that can scroll is
        cancelled here; inside one, the list scrolls and overscroll-behavior
        keeps it from handing the swipe on.
      */
      document.addEventListener('touchmove', function (e) {
        if (!document.documentElement.classList.contains('bc-search-open')) { return; }
        var panel = e.target && e.target.closest ? e.target.closest('#MobileHeaderSearchResults') : null;
        if (!panel || panel.scrollHeight <= panel.clientHeight + 1) { e.preventDefault(); }
      }, { passive: false });

      /*
        THE PHONE LIST ENDS ABOVE THE KEYBOARD (2026-09-13). It was capped at a
        flat 480px under a header ~170px down, so with the keyboard up it ran
        on beneath it: the rows under the keys could not be reached and
        nothing showed that the list scrolled. The keyboard does not shrink the
        page - it shrinks the visual viewport - so the cap is read from that:
        the visual viewport's bottom, less the list's own top, less 12px.
        Every change of that viewport (the keyboard rising, falling, changing
        height; the address bar; a turn to landscape) refits the open list in
        the same frame. Without visualViewport the window's innerHeight stands
        in - the same measure --bc-vh is written from for the viewer.
      */
      var refit = function () { self._fitMobilePanels(); };
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', refit);
        window.visualViewport.addEventListener('scroll', refit);
      }
      window.addEventListener('resize', refit);
      window.addEventListener('orientationchange', refit);
      window.addEventListener('scroll', refit, { passive: true });

      // Close dropdowns on outside click
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.predictive-search-wrapper')) {
          self._closeAll();
        }
      });

      // The open phone list also closes on a touch going down outside it. A tap on
      // something that is not clickable does not deliver a click to the document in
      // Safari, and in the emulated phone a tap on the header left the list open at
      // every width; the pointer going down arrives either way.
      document.addEventListener('pointerdown', function (e) {
        if (!document.documentElement.classList.contains('bc-search-open')) { return; }
        if (!e.target.closest || !e.target.closest('.predictive-search-wrapper')) { self._closeAll(); }
      }, true);

      // Close on Escape
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { self._closeAll(); }
      });

      // ATC button — separate listener, does NOT interfere with outside-click
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('.ps-atc-btn');
        if (btn) { self._handleAtc(btn); }
      });
    },

    /* ── Bind input events ──────────────────────────── */
    _bindInput: function (input, results, context) {
      var self = this;
      results.dataset.inputId = input.id;

      if (context === 'mobile') {
        results.addEventListener('scroll', function () { self._syncScroll(results); }, { passive: true });
      }

      // Keystroke handler with debounce
      input.addEventListener('input', function () {
        var query = input.value.trim();

        clearTimeout(self._debounceTimer);

        if (query.length < self.config.minChars) {
          self._hide(results);
          return;
        }

        if (query === self._lastQuery) { return; }

        self._debounceTimer = setTimeout(function () {
          self._fetch(query, input, results, context);
        }, self.config.debounceMs);
      });

      // Keyboard navigation
      input.addEventListener('keydown', function (e) {
        var items = results.querySelectorAll('.ps-result-item');
        if (!items.length) { return; }

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          self._activeIndex = Math.min(self._activeIndex + 1, items.length - 1);
          self._setActive(items);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          self._activeIndex = Math.max(self._activeIndex - 1, 0);
          self._setActive(items);
        } else if (e.key === 'Enter') {
          if (self._activeIndex >= 0 && items[self._activeIndex]) {
            e.preventDefault();
            items[self._activeIndex].querySelector('a').click();
          }
        }
      });

      // Re-open on focus if value present
      input.addEventListener('focus', function () {
        if (input.value.trim().length >= self.config.minChars && results.innerHTML) {
          self._show(results);
        }
      });
    },

    /* ── Fetch from Shopify API ─────────────────────── */
    _fetch: function (query, input, results, context) {
      var self = this;
      var requestId = String(++self._requestSeq);
      var shouldCheckSku = self._shouldCheckSku(query);

      results.dataset.psRequestId = requestId;

      if (self._cache[query]) {
        self._render(self._cache[query], query, results, context);
        return;
      }

      self._showLoading(results);

      var suggestRequest = self._suggest(query);

      var settle = function (data) {
        if (!self._isCurrentRequest(results, requestId, input, query)) { return; }

        if (self._productsFrom(data).length || !self._shouldZeroPad(query)) {
          self._finish(data, query, input, results, context, requestId);
          return;
        }

        // Nothing for a SKU-shaped query: ask again with leading zeros.
        self._fetchZeroPadded(query, input, results, requestId).then(function (padded) {
          self._finish(padded || data, query, input, results, context, requestId);
        });
      };

      if (shouldCheckSku) {
        Promise.all([
          suggestRequest.catch(function () { return null; }),
          self._fetchSkuFallback(query)
        ])
          .then(function (responses) {
            var data = responses[0] || self._emptySearchData();
            var fallbackProducts = responses[1] || [];

            settle(fallbackProducts.length ? self._mergeFallbackProducts(data, fallbackProducts) : data);
          })
          .catch(function () {
            if (self._isCurrentRequest(results, requestId, input, query)) {
              self._hide(results);
            }
          });
        return;
      }

      suggestRequest
        .then(settle)
        .catch(function () {
          if (self._isCurrentRequest(results, requestId, input, query)) {
            self._hide(results);
          }
        });
    },

    _suggest: function (query) {
      var url = this.config.endpoint
        + '?q=' + encodeURIComponent(query)
        + '&resources[type]=' + this.config.types
        + '&resources[limit]=' + this.config.limit
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=title,product_type,variants.title,vendor';

      return fetch(url)
        .then(function (res) {
          if (!res.ok) { throw new Error('Network response was not ok'); }
          return res.json();
        });
    },

    _productsFrom: function (data) {
      return (data && data.resources && data.resources.results && data.resources.results.products) || [];
    },

    _finish: function (data, query, input, results, context, requestId) {
      if (!this._isCurrentRequest(results, requestId, input, query)) { return; }

      this._lastQuery = query;
      this._cache[query] = data;
      this._render(data, query, results, context);
    },

    /*
      LEADING ZEROS (2026-09-17, the owner's brief).

      Shopify's search matches whole tokens and prefixes, never a substring:
      "006384" is one token, and "06384" is neither that token nor a prefix
      of it, so a buyer who drops a SKU's leading zeros finds nothing. The
      catalog audited on 2026-09-17: 1546 of 3842 SKUs begin with a zero -
      1355 with one, 100 with two, 90 with three, and one ("00004") with
      four - so an empty answer to a SKU-shaped query is asked again with
      one to four zeros in front, and the first set that answers is what
      the buyer sees.

      Three characters minimum: a one or two character query that finds
      nothing is a buyer mid-word, not a SKU, and is not worth four calls
      on every keystroke. The retries only ever run on an already empty
      result, never beside a normal one.

      Hyphens count (2026-09-17): 971 of those 1546 SKUs carry one -
      00017-15, 06616-15 - so the query may hold hyphens as well as
      digits. It must BEGIN and END with a digit, so a stray "-" or "--",
      or a half-typed "17-", starts no chain, and the zeros go on the
      FRONT of the whole string only: "17-15" is asked as "017-15",
      "0017-15", "00017-15" - never padded after the hyphen.
    */
    _shouldZeroPad: function (query) {
      return query.length >= this.config.zeroPadMinChars && /^\d[\d-]*\d$/.test(query);
    },

    _fetchZeroPadded: function (query, input, results, requestId) {
      var self = this;
      var chain = Promise.resolve(null);

      for (var depth = 1; depth <= self.config.zeroPadMax; depth++) {
        chain = chain.then((function (candidate) {
          return function (found) {
            if (found) { return found; }
            // The buyer typed on: the rest of the chain is never asked.
            if (!self._isCurrentRequest(results, requestId, input, query)) { return null; }

            return self._suggest(candidate)
              .then(function (data) { return self._productsFrom(data).length ? data : null; })
              .catch(function () { return null; });
          };
        })(new Array(depth + 1).join('0') + query));
      }

      return chain.catch(function () { return null; });
    },

    _fetchSkuFallback: function (query) {
      var self = this;
      var url = '/search?q=' + encodeURIComponent(query) + '&type=product&view=samitaWSProductsJson';

      return fetch(url)
        .then(function (res) {
          if (!res.ok) { throw new Error('Fallback response was not ok'); }
          return res.text();
        })
        .then(function (raw) {
          try {
            return JSON.parse(raw);
          } catch (error) {
            return [];
          }
        })
        .then(function (products) {
          var normalizedQuery = self._normalizeSku(query);
          if (!normalizedQuery) { return []; }

          return (Array.isArray(products) ? products : [])
            .filter(function (product) {
              return (product.variants || []).some(function (variant) {
                return self._normalizeSku(variant.sku).indexOf(normalizedQuery) !== -1;
              });
            })
            .slice(0, self.config.limit)
            .map(function (product) {
              return self._transformFallbackProduct(product);
            });
        })
        .catch(function () {
          return [];
        });
    },

    _mergeFallbackProducts: function (data, fallbackProducts) {
      var merged = data || {};
      merged.resources = merged.resources || {};
      merged.resources.results = merged.resources.results || {};
      merged.resources.results.products = fallbackProducts;
      return merged;
    },

    _normalizeSku: function (value) {
      return String(value || '').replace(/[\s-]+/g, '').toLowerCase();
    },

    _shouldCheckSku: function (query) {
      var normalizedQuery = this._normalizeSku(query);
      return normalizedQuery.length >= this.config.minChars && /\d/.test(normalizedQuery);
    },

    _isCurrentRequest: function (results, requestId, input, query) {
      return results.dataset.psRequestId === requestId && input.value.trim() === query;
    },

    _emptySearchData: function () {
      return { resources: { results: { products: [] } } };
    },

    _transformFallbackProduct: function (product) {
      var imageUrl = '';

      if (product.images && product.images.length && product.images[0].src) {
        imageUrl = product.images[0].src;
      } else if (product.featured_image) {
        imageUrl = product.featured_image;
      }

      if (imageUrl.indexOf('//') === 0) {
        imageUrl = 'https:' + imageUrl;
      } else if (imageUrl.indexOf('files/') === 0) {
        imageUrl = 'https://billiard.city/cdn/shop/' + imageUrl;
      }

      return {
        title: product.title,
        url: product.url,
        featured_image: imageUrl ? { url: imageUrl } : null,
        price: (parseInt(product.price, 10) || 0) / 100,
        compare_at_price_max: (parseInt(product.compare_at_price_max, 10) || 0) / 100,
        available: product.available !== false,
        vendor: product.vendor || '',
        handle: product.handle
      };
    },

    _render: function (data, query, results, context) {
      var self   = this;
      var items  = data.resources && data.resources.results && data.resources.results.products
        ? data.resources.results.products
        : [];

      self._activeIndex = -1;

      if (!items.length) {
        results.innerHTML = '<div class="ps-no-results">No results for <strong>"' + self._escape(query) + '"</strong></div>';
        self._show(results);
        return;
      }

      var listboxId = results.id + '-list';
      var html = '<ul id="' + listboxId + '" class="ps-results-list">';

      items.forEach(function (product, index) {
        var optionId = listboxId + '-option-' + index;
        var imgSrc   = product.featured_image && product.featured_image.url
          ? product.featured_image.url + '&width=80'
          : null;

        var title    = self._highlight(product.title, query);
        var price    = product.price ? self._formatMoney(product.price) : '';
        var compared = product.compare_at_price_max && product.compare_at_price_max > product.price
          ? self._formatMoney(product.compare_at_price_max) : '';

        var available = product.available !== false;

        html += '<li id="' + optionId + '" class="ps-result-item">';
        html += '<a href="' + product.url + '" class="ps-result-link" tabindex="-1">';

        if (imgSrc) {
          html += '<div class="ps-result-img-wrap">';
          html += '<img src="' + imgSrc + '" alt="' + self._escape(product.title) + '" class="ps-result-img" loading="lazy" width="56" height="56">';
          html += '</div>';
        } else {
          html += '<div class="ps-result-img-wrap ps-result-img-wrap--placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="6" fill="#f0f0f0"/><path d="M14 26l6-8 4 5 3-3 5 6H14z" fill="#ccc"/><circle cx="17" cy="18" r="2.5" fill="#ccc"/></svg></div>';
        }

        html += '<div class="ps-result-info">';
        html += '<span class="ps-result-title">' + title + '</span>';

        if (product.vendor) {
          html += '<span class="ps-result-vendor">' + self._escape(product.vendor) + '</span>';
        }

        html += '<div class="ps-result-price-row">';
        if (price) {
          html += '<span class="ps-result-price">' + price + '</span>';
        }
        if (compared) {
          html += '<span class="ps-result-compare">' + compared + '</span>';
        }
        if (!available) {
          html += '<span class="ps-result-badge">Sold out</span>';
        }
        html += '</div>';  // .ps-result-price-row

        html += '</div>';  // .ps-result-info
        html += '</a>';

        if (context === 'mobile' && available) {
          html += '<button type="button" class="ps-atc-btn" data-handle="' + self._escape(product.handle) + '" aria-label="Add to cart">'
            + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">'
            + '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>'
            + '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>'
            + '<line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>'
            + '</svg></button>';
        }

        html += '</li>';
      });

      html += '</ul>';

      // "View all results" footer
      html += '<div class="ps-results-footer">';
      html += '<a href="/search?q=' + encodeURIComponent(query) + '&type=product" class="ps-view-all">See all results for <em>"' + self._escape(query) + '"</em></a>';
      html += '</div>';

      results.innerHTML = html;
      self._show(results);
    },

    /* ── Loading state ──────────────────────────────── */
    _showLoading: function (results) {
      results.innerHTML = '<div class="ps-loading"><span class="ps-loading-dot"></span><span class="ps-loading-dot"></span><span class="ps-loading-dot"></span></div>';
      this._show(results);
    },

    /* ── Keyboard navigation ────────────────────────── */
    _setActive: function (items) {
      var input = items.length ? this._getInput(items[0].closest('.predictive-search__results')) : null;
      items.forEach(function (item, idx) {
        var isActive = idx === PredictiveSearch._activeIndex;
        item.classList.toggle('ps-result-item--active', isActive);
      });      if (items[this._activeIndex]) {
        items[this._activeIndex].scrollIntoView({ block: 'nearest' });
      }
    },

    /* ── Visibility helpers ─────────────────────────── */
    _show: function (el) {
      el.removeAttribute('hidden');
      if (this._mobilePanels.indexOf(el) !== -1) {
        document.documentElement.classList.add('bc-search-open');
        this._fitPanel(el);
      }
    },

    /* ── Phone list height from the visible window ─── */
    _visibleBottom: function () {
      var vv = window.visualViewport;
      if (vv) { return vv.offsetTop + vv.height; }
      var h = window.innerHeight || document.documentElement.clientHeight;
      document.documentElement.style.setProperty('--bc-vh', (h / 100) + 'px');
      return h;
    },

    _fitPanel: function (el) {
      if (!el || el.hasAttribute('hidden')) { return; }
      var room = this._visibleBottom() - el.getBoundingClientRect().top - this.config.viewportGap;
      el.style.maxHeight = Math.max(0, Math.floor(room)) + 'px';
      el.setAttribute('data-ps-viewport', window.visualViewport ? 'visualViewport' : 'innerHeight');
      this._syncScroll(el);
    },

    _fitMobilePanels: function () {
      var self = this;
      self._mobilePanels.forEach(function (el) { self._fitPanel(el); });
    },

    /*
      THE LIST SAYS IT SCROLLS. is-scrollable and is-scrolled-end drive the
      24px fade at its foot (assets/bc-responsive.css, section 8): shown
      while there is more below, gone at the end. The scrollbar is styled
      thin and permanent there too, but a phone engine with overlay
      scrollbars - Safari, Chrome for Android - hides it whatever the CSS
      says, and that is the scrollbar the owner could not see. So where the
      native bar takes no width, a 4px thumb of our own is drawn over the
      list's right edge and kept in step with the scroll; where the bar has
      width it is already on screen and nothing is drawn.
    */
    _syncScroll: function (el) {
      var thumb = this._thumbFor(el);
      if (el.hasAttribute('hidden')) { thumb.hidden = true; return; }
      var scrollable = el.scrollHeight > el.clientHeight + 1;
      var atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      el.classList.toggle('is-scrollable', scrollable);
      el.classList.toggle('is-scrolled-end', !scrollable || atEnd);
      var cs = window.getComputedStyle(el);
      var borderTop = parseFloat(cs.borderTopWidth) || 0;
      var gutter = el.offsetWidth - el.clientWidth - (parseFloat(cs.borderLeftWidth) || 0) - (parseFloat(cs.borderRightWidth) || 0);
      if (!scrollable || gutter > 0) { thumb.hidden = true; return; }
      var inset = 8;
      var track = el.clientHeight - inset * 2;
      var size = Math.max(28, track * el.clientHeight / el.scrollHeight);
      var travel = el.scrollHeight - el.clientHeight;
      thumb.style.top = (el.offsetTop + borderTop + inset + (track - size) * (el.scrollTop / travel)) + 'px';
      thumb.style.left = (el.offsetLeft + el.offsetWidth - 7) + 'px';
      thumb.style.height = size + 'px';
      thumb.hidden = false;
    },

    _thumbFor: function (el) {
      var thumb = el.parentNode.querySelector('.ps-scroll-thumb');
      if (!thumb) {
        thumb = document.createElement('span');
        thumb.className = 'ps-scroll-thumb';
        thumb.setAttribute('aria-hidden', 'true');
        thumb.hidden = true;
        el.parentNode.appendChild(thumb);
      }
      return thumb;
    },

    _hide: function (el) {
      el.setAttribute('hidden', '');      this._activeIndex = -1;
      if (this._mobilePanels.indexOf(el) !== -1) {
        document.documentElement.classList.remove('bc-search-open');
        this._syncScroll(el);
      }
    },

    /*
      The clear button (sections/header.liquid): the theme hides the
      browser's own cross on search fields. It shows while the field holds
      text; a tap empties the field, closes the list and leaves the focus -
      and so the keyboard - in the field. mousedown is cancelled so a mouse
      click does not take the focus first; Safari does not focus buttons on a
      tap. _lastQuery is forgotten, or typing the same query again would be
      ignored as unchanged.
    */
    _bindClear: function (input, results, button) {
      var self = this;
      var sync = function () { button.hidden = input.value.length === 0; };
      input.addEventListener('input', sync);
      button.addEventListener('mousedown', function (e) { e.preventDefault(); });
      button.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(self._debounceTimer);
        input.value = '';
        self._lastQuery = '';
        self._hide(results);
        sync();
        input.focus();
      });
      sync();
    },

    _closeAll: function () {
      var self = this;
      ['DesktopStickySearchResults', 'DesktopSearchResults', 'MobileHeaderSearchResults'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) { self._hide(el); }
      });
    },

    _getInput: function (results) {
      if (!results || !results.dataset || !results.dataset.inputId) {
        return null;
      }
      return document.getElementById(results.dataset.inputId);
    },

    /* ── Utility ────────────────────────────────────── */
    _highlight: function (text, query) {
      if (!query) { return this._escape(text); }
      var escaped  = this._escape(text);
      var safeQ    = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var regex    = new RegExp('(' + safeQ + ')', 'gi');
      return escaped.replace(regex, '<' + this.config.highlightTag + '>$1</' + this.config.highlightTag + '>');
    },

    _escape: function (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    _formatMoney: function (value) {
      var num = parseFloat(value);
      if (isNaN(num)) { return ''; }
      var parts  = num.toFixed(2).split('.');
      parts[0]   = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts.join('.');
    },

    _handleAtc: function (btn) {
      var self = this;
      var handle = btn.dataset.handle;
      if (!handle || btn.disabled) { return; }
      btn.disabled = true;
      btn.classList.add('ps-atc-btn--loading');
      fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var variants = product.variants || [];
          var avail = variants.filter(function (v) { return v.available; });
          var pool = avail.length ? avail : variants;
          if (pool.length === 1) {
            self._doAdd(pool[0].id, btn);
          } else if (pool.length > 1) {
            btn.classList.remove('ps-atc-btn--loading');
            btn.disabled = false;
            self._showPicker(product, pool);
          } else {
            btn.classList.remove('ps-atc-btn--loading');
            btn.disabled = false;
          }
        })
        .catch(function () {
          btn.classList.remove('ps-atc-btn--loading');
          btn.disabled = false;
        });
    },

    _doAdd: function (variantId, btn) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (r) { if (!r.ok) { throw 0; } return r.json(); })
        .then(function () {
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
            document.querySelectorAll('#CartCount,.mobile-header__cart-count,.cart-count')
              .forEach(function (el) { el.textContent = c.item_count; });
          });
          if (btn) {
            btn.classList.remove('ps-atc-btn--loading');
            btn.classList.add('ps-atc-btn--success');
            setTimeout(function () { btn.classList.remove('ps-atc-btn--success'); btn.disabled = false; }, 1800);
          }
        })
        .catch(function () {
          if (btn) { btn.classList.remove('ps-atc-btn--loading'); btn.disabled = false; }
        });
    },

    _showPicker: function (product, variants) {
      var self = this;
      var old = document.getElementById('ps-picker-overlay');
      if (old) { old.parentNode.removeChild(old); }
      var overlay = document.createElement('div');
      overlay.id = 'ps-picker-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0);display:flex;align-items:flex-end;transition:background .2s';
      var sheet = document.createElement('div');
      sheet.style.cssText = 'width:100%;background:#fff;border-radius:20px 20px 0 0;padding:0 0 24px;transform:translateY(100%);transition:transform .25s cubic-bezier(.33,1,.68,1);box-shadow:0 -8px 32px rgba(0,0,0,.15);max-height:70vh;overflow-y:auto';
      var opts = variants.map(function (v) {
        var price = v.price ? ' — $' + (v.price/100).toFixed(2) : '';
        return '<option value="'+v.id+'">'+self._escape(v.title)+price+'</option>';
      }).join('');
      sheet.innerHTML =
        '<div style="width:40px;height:4px;background:rgba(0,0,0,.15);border-radius:2px;margin:12px auto 0"></div>'
        +'<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 20px 10px;border-bottom:1px solid rgba(0,0,0,.07)">'
        +'<span style="font-size:15px;font-weight:600;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'+self._escape(product.title)+'</span>'
        +'<button id="ps-picker-close" type="button" style="width:30px;height:30px;border-radius:50%;border:none;background:rgba(0,0,0,.07);cursor:pointer;display:flex;align-items:center;justify-content:center">'
        +'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button></div>'
        +'<div style="padding:16px 20px">'
        +'<label style="display:block;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#888;margin-bottom:8px">Choose option</label>'
        +'<select id="ps-picker-select" style="width:100%;padding:12px 16px;font-size:15px;border:1.5px solid rgba(0,0,0,.15);border-radius:10px;background:#f5f5f5;-webkit-appearance:none;appearance:none;outline:none">'+opts+'</select></div>'
        +'<div style="padding:0 20px">'
        +'<button id="ps-picker-confirm" type="button" style="width:100%;padding:14px;font-size:16px;font-weight:600;color:#fff;background:#1a1a1a;border:none;border-radius:12px;cursor:pointer">Add to Cart</button></div>';
      overlay.appendChild(sheet);
      document.body.appendChild(overlay);
      requestAnimationFrame(function () {
        overlay.style.background = 'rgba(0,0,0,.45)';
        sheet.style.transform = 'translateY(0)';
      });
      function close() {
        overlay.style.background = 'rgba(0,0,0,0)';
        sheet.style.transform = 'translateY(100%)';
        setTimeout(function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }, 250);
      }
      document.getElementById('ps-picker-close').onclick = close;
      overlay.onclick = function (e) { if (e.target === overlay) { close(); } };
      document.getElementById('ps-picker-confirm').onclick = function () {
        var sel = document.getElementById('ps-picker-select');
        if (sel && sel.value) { self._doAdd(sel.value, null); close(); }
      };
    }
  };

  /* ── Boot ───────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { PredictiveSearch.init(); });
  } else {
    PredictiveSearch.init();
  }

  window.PredictiveSearch = PredictiveSearch;

})();

